import type { AuditView } from "~/ui-rtk/api/auditsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { auditTypeMeta } from "../constants"
import { type AuditItem, describeItem, itemName } from "../utils"

/** "targetDailyBudget" → "target Daily Budget" (CSS uppercases it). */
const labelize = (key: string) => key.replace(/([A-Z])/g, " $1")

function ContextGrid({ context }: { context: Record<string, unknown> }) {
  const entries = Object.entries(context).filter(
    ([, value]) => value !== null && value !== undefined,
  )
  if (entries.length === 0) return null
  return (
    <div className="grid grid-cols-2 gap-3 rounded-lg border border-system-blue-600/30 p-3 sm:grid-cols-3">
      {entries.map(([key, value]) => (
        <div key={key} className="min-w-0">
          <div className="truncate text-[11px] uppercase tracking-wide text-system-gray-500">
            {labelize(key)}
          </div>
          <div className="break-words text-sm text-system-gray-200">
            {typeof value === "object" ? JSON.stringify(value) : String(value)}
          </div>
        </div>
      ))}
    </div>
  )
}

/** Read-only detail view of one recorded action, opened from the calendar. */
export default function AuditDialog({
  audit,
  onClose,
}: {
  audit: AuditView | null
  onClose: () => void
}) {
  const dayjs = useDate()
  const items = (audit?.items ?? []) as AuditItem[]

  return (
    <Dialog
      isOpen={Boolean(audit)}
      handleClose={onClose}
      width={640}
      closeOnClickOutside
      classes={{
        container:
          "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl",
      }}
    >
      {audit && (
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
                <span
                  className={cn(
                    "h-2.5 w-2.5 shrink-0 rounded-full",
                    auditTypeMeta(audit.type).dotClass,
                  )}
                />
                {auditTypeMeta(audit.type).label}
              </h3>
              <p className="text-xs text-system-gray-500">
                {dayjs(audit.createdAt).format("MMM D, YYYY HH:mm")}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 text-xs">
              <span className="rounded bg-white/10 px-2 py-0.5 text-system-gray-200">
                {audit.total} item{audit.total === 1 ? "" : "s"}
              </span>
              {audit.successful > 0 && (
                <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-emerald-200">
                  {audit.successful} ok
                </span>
              )}
              {audit.failed > 0 && (
                <span className="rounded bg-red-500/20 px-2 py-0.5 text-red-200">
                  {audit.failed} failed
                </span>
              )}
            </div>
          </div>

          {audit.context && <ContextGrid context={audit.context} />}

          <div className="max-h-96 overflow-y-auto rounded-lg border border-system-blue-600/30">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-system-blue-800 text-[11px] uppercase tracking-wide text-system-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Entity</th>
                  <th className="px-3 py-2 font-medium">Change</th>
                  <th className="px-3 py-2 font-medium">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr
                    key={index}
                    className="border-t border-system-blue-600/20 align-top hover:bg-white/5"
                  >
                    <td className="max-w-[220px] truncate px-3 py-2 text-system-gray-200">
                      {itemName(item, index)}
                    </td>
                    <td className="px-3 py-2 text-system-gray-400">
                      {describeItem(audit.type, item) || "—"}
                    </td>
                    <td className="max-w-[180px] px-3 py-2">
                      {item.success === false ? (
                        <span
                          className="block truncate text-red-300"
                          title={
                            typeof item.error === "string"
                              ? item.error
                              : undefined
                          }
                        >
                          ✕ {typeof item.error === "string" ? item.error : ""}
                        </span>
                      ) : (
                        <span className="text-emerald-300">✓</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <details className="text-xs text-system-gray-400">
            <summary className="cursor-pointer select-none hover:text-white">
              Raw data
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded bg-system-blue-800/50 p-3">
              {JSON.stringify(
                { context: audit.context, items: audit.items },
                null,
                2,
              )}
            </pre>
          </details>

          <div className="flex justify-end border-t border-system-blue-600/30 pt-4">
            <Button
              variant={{ variant: "text" }}
              size="sm"
              type="button"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  )
}
